import { degreeToRadian, radianToDegree } from "common/utils/math";
import { DIST_EPSILON } from "geom/core";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetween, vec2Dot, vec2Perp, vec2Rotate, vec2Scale } from "geom/vec2";

interface RowAlignedItem<T> {
  readonly item: T;
  readonly orientation: Vec2;
  readonly width: number;
  readonly u: number;
  readonly v: number;
  adjust: number;
  moveScore: number;
}

export interface AlignmentInfo {
  readonly orientation: Vec2;
  readonly x: number;
  readonly y: number;
  readonly width: number;
}

export interface PositionInfo<T> {
  readonly item: T;
  readonly xAdjust: number;
  readonly yAdjust: number;
}

export function fixOverlappingOrientedItemsInRows<T>(
  items: T[],
  verticalGroupTolerance: number,
  horizontalPadWidth: number,
  inAdapter: (item: T) => AlignmentInfo,
  outAdapter: (rowItem: PositionInfo<T>) => T,
): T[] {
  const itemByGroupHash: { [groupHash: number]: RowAlignedItem<T>[] } = {};

  for (const rawItem of items) {
    const { orientation, x, y, width } = inAdapter(rawItem);
    const [tX, tY] = orientation;
    const angle = Math.round(radianToDegree(vec2AngleBetween(1, 0, tX, tY)));
    const [sX, sY] = vec2Rotate(1, 0, degreeToRadian(angle));

    const u = vec2Dot(sX, sY, x, y);
    const [rX, rY] = vec2Perp(sX, sY);
    const v = vec2Dot(rX, rY, x, y);

    const item: RowAlignedItem<T> = {
      item: rawItem,
      orientation: [tX, tY],
      u,
      v,
      adjust: 0,
      moveScore: 0,
      width,
    };

    const group: RowAlignedItem<T>[] = itemByGroupHash[angle] || [];
    group.push(item);
    itemByGroupHash[angle] = group;
  }

  const adjustedItems = fixOverlappingItemsInRows(
    Object.values(itemByGroupHash),
    verticalGroupTolerance,
    horizontalPadWidth,
  );

  const newItems: T[] = [];
  for (const adjustedItem of adjustedItems) {
    const { orientation, adjust, item } = adjustedItem;
    if (Math.abs(adjust) < DIST_EPSILON) {
      newItems.push(outAdapter({ item, xAdjust: 0, yAdjust: 0 }));
    } else {
      const [tX, tY] = orientation;
      const [aX, aY] = vec2Scale(tX, tY, adjust);
      newItems.push(outAdapter({ item, xAdjust: aX, yAdjust: aY }));
    }
  }

  return newItems;
}

function fixOverlappingItemsInRows<T>(
  groupedItems: RowAlignedItem<T>[][],
  verticalGroupTolerance: number,
  horizontalPadWidth: number,
): RowAlignedItem<T>[] {
  const flatListOfAdjustedItems: RowAlignedItem<T>[] = [];

  for (const items of groupedItems) {
    const rows: RowAlignedItem<T>[][] = makeRowsByVerticalPosition(items, verticalGroupTolerance);
    for (const row of rows) {
      if (row.length > 1) {
        fixOverlappingItemsInRow(row, horizontalPadWidth);
      }
      for (const item of row) {
        flatListOfAdjustedItems.push(item);
      }
    }
  }

  return flatListOfAdjustedItems;
}

function makeRowsByVerticalPosition<T>(
  items: RowAlignedItem<T>[],
  verticalGroupTolerance: number,
): RowAlignedItem<T>[][] {
  items.sort((a, b) => a.v - b.v);

  // get starting place
  let curV = items[0].v;
  let curRowIndex = 0;
  const rows: RowAlignedItem<T>[][] = [];
  rows[0] = [];
  for (const item of items) {
    const { v } = item;
    if (Math.abs(curV - v) <= verticalGroupTolerance) {
      rows[curRowIndex].push(item);
    } else {
      curRowIndex++;
      rows[curRowIndex] = [];
      rows[curRowIndex].push(item);
    }
    curV = v;
  }
  return rows;
}

function fixOverlappingItemsInRow<T>(row: RowAlignedItem<T>[], horizontalPadWidth: number) {
  row.sort((a, b) => a.u - b.u);

  let i: number = 0;
  let dir = 1;
  let switchDirIndex: number | undefined = undefined;
  let stepCount = 0;

  // set reasonable limit if too many steps are required
  const stepLimit = row.length * 4;

  while (stepCount < stepLimit && i >= 0 && i < row.length) {
    stepCount++;
    // if next move is out of range, switch directions if switchDirIndex exists
    if ((i + dir < 0 || i + dir > row.length - 1) && switchDirIndex !== undefined) {
      dir = -dir;
      i = switchDirIndex;
      switchDirIndex = undefined;
    }
    const curItem = row[i];
    const nextItem = row[i + dir];

    let distance: number;
    if (dir > 0) {
      distance = nextItem
        ? nextItem.u + nextItem.adjust - nextItem.width / 2 - (curItem.u + curItem.adjust + curItem.width / 2)
        : Number.MAX_VALUE;
    } else {
      distance = nextItem
        ? curItem.u + curItem.adjust - curItem.width / 2 - (nextItem.u + nextItem.adjust + nextItem.width / 2)
        : Number.MAX_VALUE;
    }

    if (distance < horizontalPadWidth) {
      const prevItem = row[i - dir];
      if (prevItem !== undefined && prevItem.moveScore <= nextItem.moveScore) {
        const halfShiftAmount = (horizontalPadWidth - distance) / 2;

        curItem.adjust += -dir * halfShiftAmount;
        curItem.moveScore += halfShiftAmount;

        switchDirIndex = i + dir;
        nextItem.adjust += dir * halfShiftAmount;
        nextItem.moveScore += halfShiftAmount;
      } else if (prevItem === undefined) {
        const fullShiftAmount = horizontalPadWidth - distance;
        curItem.adjust += -dir * fullShiftAmount;
        curItem.moveScore += fullShiftAmount;
      } else {
        const fullShiftAmount = horizontalPadWidth - distance;
        nextItem.adjust += dir * fullShiftAmount;
        nextItem.moveScore += fullShiftAmount;
      }
    }
    i += dir;
  }
}
